"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../lib/utils.js";

interface AccordionProps {
  type?: "single" | "multiple";
  collapsible?: boolean;
  defaultValue?: string | string[];
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  children: React.ReactNode;
  className?: string;
}

interface AccordionItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

interface AccordionTriggerProps {
  children: React.ReactNode;
  className?: string;
}

interface AccordionContentProps {
  children: React.ReactNode;
  className?: string;
}

const AccordionContext = React.createContext<{
  openItems: Set<string>;
  toggleItem: (value: string) => void;
}>({
  openItems: new Set(),
  toggleItem: () => {},
});

// Context for passing item value to trigger and content
const ItemValueContext = React.createContext<string>("");

export const Accordion: React.FC<AccordionProps> = ({
  type = "single",
  collapsible = true,
  defaultValue,
  value: controlledValue,
  onValueChange,
  children,
  className,
}) => {
  const initialValue = React.useMemo(() => {
    if (defaultValue) {
      return Array.isArray(defaultValue) ? new Set(defaultValue) : new Set([defaultValue]);
    }
    return new Set<string>();
  }, [defaultValue]);

  const [openItems, setOpenItems] = React.useState<Set<string>>(initialValue);
  
  // Use controlled value if provided
  const currentValue = controlledValue !== undefined 
    ? (Array.isArray(controlledValue) ? new Set(controlledValue) : new Set([controlledValue]))
    : openItems;

  const toggleItem = React.useCallback((value: string) => {
    const updateItems = (prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        if (type === "single") {
          newSet.clear();
        }
        newSet.add(value);
      }
      return newSet;
    };

    if (controlledValue !== undefined) {
      // Controlled mode
      const newSet = updateItems(currentValue);
      const newValue = type === "single" 
        ? (newSet.values().next().value || "")
        : Array.from(newSet);
      onValueChange?.(newValue);
    } else {
      // Uncontrolled mode
      setOpenItems(updateItems);
    }
  }, [type, controlledValue, currentValue, onValueChange]);

  return (
    <AccordionContext.Provider value={{ openItems: currentValue, toggleItem }}>
      <div className={cn("w-full", className)}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

export const AccordionItem: React.FC<AccordionItemProps> = ({
  value,
  children,
  className,
}) => {
  return (
    <ItemValueContext.Provider value={value}>
      <div className={cn("border-b border-border", className)}>
        {children}
      </div>
    </ItemValueContext.Provider>
  );
};

export const AccordionTrigger: React.FC<AccordionTriggerProps> = ({
  children,
  className,
}) => {
  const { toggleItem, openItems } = React.useContext(AccordionContext);
  const itemValue = React.useContext(ItemValueContext);
  const isOpen = openItems.has(itemValue);

  return (
    <button
      type="button"
      onClick={() => toggleItem(itemValue)}
      className={cn(
        "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
        className,
      )}
    >
      {children}
      <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform duration-200", isOpen && "rotate-180")} />
    </button>
  );
};

export const AccordionContent: React.FC<AccordionContentProps> = ({
  children,
  className,
}) => {
  const { openItems } = React.useContext(AccordionContext);
  const itemValue = React.useContext(ItemValueContext);
  const isOpen = openItems.has(itemValue);

  if (!isOpen) return null;

  return (
    <div className={cn("overflow-hidden text-sm transition-all", className)}>
      <div className={cn("pb-4 pt-0", className)}>{children}</div>
    </div>
  );
};

// Wrapper component to provide item value context (kept for backward compatibility)
export const AccordionItemWithContext: React.FC<AccordionItemProps> = ({
  value,
  children,
  className,
}) => {
  return (
    <AccordionItem value={value} className={className}>
      {children}
    </AccordionItem>
  );
};
